import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageLoader from '../components/Common/PageLoader';

/**
 * Hidden admin login page at /dash-control.
 * NOT linked from anywhere in the public UI — only accessible by URL.
 * Bookmark this URL; share only with trusted admins.
 */
export default function AdminLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginSso, ssoEnabled, localEnabled, canSeeDashboard } = useAuth();
  const navigate = useNavigate();
  const [ssoLoading, setSsoLoading] = useState(false);

  // Already logged in? Go straight to admin
  if (canSeeDashboard) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const role = await login(username, password);
      if (role !== 'admin') {
        setError('This account does not have admin access.');
        setLoading(false);
        return;
      }
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const onSsoSubmit = async () => {
    setError('');
    setSsoLoading(true);
    try {
      // Redirect-flow: the page navigates away to Entra ID and comes back
      // via /auth/callback.  No role returned here — AuthContext finishes
      // the exchange on the callback and SsoCallbackPage routes to /admin.
      await loginSso();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Microsoft sign-in failed.';
      setError(msg);
      setSsoLoading(false);
    }
  };

  const s = {
    wrap: {
      minHeight: 'calc(100vh - 140px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px',
      background: 'linear-gradient(135deg, #f0fafa 0%, #e6f7f7 50%, #d1f0f0 100%)',
    },
    card: {
      width: '100%', maxWidth: '420px',
      background: '#ffffff',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0 20px 50px rgba(13, 79, 82, 0.12), 0 4px 12px rgba(0,0,0,0.05)',
      border: '1px solid rgba(29,156,156,0.18)',
      position: 'relative',
      overflow: 'hidden',
    },
    accent: {
      position: 'absolute', top: 0, left: 0, right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #0d4f52, #1d9c9c, #4fd1c5)',
    },
    badge: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '5px 12px',
      background: 'rgba(29,156,156,0.1)',
      color: '#0d7377',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      border: '1px solid rgba(29,156,156,0.25)',
      marginBottom: '18px',
    },
    title: { fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '6px', letterSpacing: '-0.3px' },
    sub: { color: '#64748b', fontSize: '13.5px', marginBottom: '26px' },
    label: { display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' },
    input: {
      width: '100%',
      padding: '11px 14px',
      borderRadius: '10px',
      border: '1px solid #cbd5e1',
      fontSize: '14px',
      marginBottom: '14px',
      outline: 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      boxSizing: 'border-box',
    },
    btn: {
      width: '100%',
      padding: '12px 16px',
      background: 'linear-gradient(135deg, #0d4f52 0%, #1d9c9c 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '999px',
      fontSize: '14px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.18s',
      boxShadow: '0 4px 12px rgba(13, 79, 82, 0.28)',
      letterSpacing: '0.02em',
      marginTop: '6px',
    },
    error: {
      padding: '10px 12px',
      background: 'rgba(239, 68, 68, 0.08)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      color: '#dc2626',
      fontSize: '13px',
      marginBottom: '14px',
    },
  };

  // While the SSO redirect is being kicked off, the page hasn't navigated yet
  // (MSAL is preparing the authorize URL).  Show a full-screen loader so the
  // user sees immediate feedback instead of a "frozen" login form.
  if (ssoLoading) {
    return (
      <PageLoader
        message="Redirecting to Microsoft"
        sub="You'll be asked to sign in with your DeliverHealth account."
      />
    );
  }

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.accent} />
        <div style={s.badge}>🔒 Restricted Access</div>
        <h1 style={s.title}>Admin Sign In</h1>
        <p style={s.sub}>Authorized personnel only.</p>

        {/* SSO — always rendered; disabled-preview until IT delivers Tenant/Client IDs */}
        {(() => {
          const ssoReady = ssoEnabled;
          return (
            <>
              <button
                type="button"
                onClick={ssoReady ? onSsoSubmit : undefined}
                disabled={!ssoReady || ssoLoading || loading}
                title={ssoReady
                  ? 'Sign in with your DeliverHealth Microsoft account'
                  : 'SSO will activate once IT provides the Tenant ID / Client ID.'}
                style={{
                  width: '100%',
                  padding: '11px 12px',
                  background: ssoReady ? '#fff' : '#f1f5f5',
                  color: ssoReady ? '#2f2f2f' : '#94a3b8',
                  border: `1px solid ${ssoReady ? '#d2d6dc' : '#e2e8f0'}`,
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  cursor: ssoReady ? (ssoLoading ? 'wait' : 'pointer') : 'not-allowed',
                  marginBottom: 8,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { if (ssoReady && !ssoLoading) e.currentTarget.style.background = '#f7fafa'; }}
                onMouseLeave={(e) => { if (ssoReady) e.currentTarget.style.background = '#fff'; }}
              >
                <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true" style={{ opacity: ssoReady ? 1 : 0.45 }}>
                  <rect width="10" height="10" x="1"  y="1"  fill="#F25022" />
                  <rect width="10" height="10" x="12" y="1"  fill="#7FBA00" />
                  <rect width="10" height="10" x="1"  y="12" fill="#00A4EF" />
                  <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
                </svg>
                {ssoLoading ? 'Signing in…' : 'Sign in with Microsoft'}
              </button>

              {!ssoReady && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  fontSize: 11, color: '#0d7377',
                  background: 'rgba(29,156,156,0.10)',
                  border: '1px solid rgba(29,156,156,0.25)',
                  borderRadius: 6,
                  padding: '5px 10px',
                  marginBottom: 14,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1d9c9c', display: 'inline-block' }} />
                  <span><strong>Preview</strong> — activates once IT completes Entra ID setup</span>
                </div>
              )}

              {localEnabled && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 11, color: '#64748b',
                  margin: ssoReady ? '10px 0 16px' : '0 0 16px',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>
                  <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                  <span>or</span>
                  <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                </div>
              )}
            </>
          );
        })()}

        <form onSubmit={onSubmit}>
          <label style={s.label}>Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            required
            style={s.input}
            onFocus={(e) => { e.target.style.borderColor = '#1d9c9c'; e.target.style.boxShadow = '0 0 0 3px rgba(29,156,156,0.12)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.boxShadow = 'none'; }}
          />

          <label style={s.label}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={s.input}
            onFocus={(e) => { e.target.style.borderColor = '#1d9c9c'; e.target.style.boxShadow = '0 0 0 3px rgba(29,156,156,0.12)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.boxShadow = 'none'; }}
          />

          {error && <div style={s.error}>{error}</div>}

          <button
            type="submit"
            disabled={loading || !username || !password}
            style={{ ...s.btn, opacity: (loading || !username || !password) ? 0.6 : 1, cursor: (loading || !username || !password) ? 'not-allowed' : 'pointer' }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
